import React, { useRef, useEffect, useState } from "react";
import { BuildingFacilityTagModel } from "src/api/building/commonfacility-types";
import { BaseTooltip } from "src/components/BaseTooltip";
import TagChip from "src/components/TagChip";

type Props = {
  tags: BuildingFacilityTagModel[];
  columnWidth: number;
};

const FacilityTagChips = ({ tags, columnWidth }: Props) => {
  // DOM 요소 참조 (실제 렌더링된 스타일 측정용)

  const measureRef = useRef<HTMLDivElement>(null);

  // 실제 적용된 폰트 스타일 (초기값: fallback)
  const [fontStyle, setFontStyle] = useState({ font: "14px sans-serif", padding: 20, gap: 5 });

  // 마운트 시 실제 DOM 스타일 측정
  useEffect(() => {
    if (measureRef.current) {
      const computed = window.getComputedStyle(measureRef.current); // 부모 div 스타일
      const childComputed = window.getComputedStyle(measureRef.current.firstElementChild || measureRef.current); // TagChip 스타일
      setFontStyle({
        font: computed.font, // 실제 폰트 (크기 + 패밀리)
        padding: parseFloat(childComputed.paddingLeft) + parseFloat(childComputed.paddingRight), // 좌우 padding 합
        gap: parseFloat(computed.gap) || 0, // flex gap
      });
    }
  }, [tags]);

  // Canvas를 이용한 정확한 텍스트 너비 측정 (픽셀 단위)
  const measureTextWidth = (text: string): number => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return text.length * 10; // context 없으면 대략 계산
    context.font = fontStyle.font; // 실제 폰트 적용

    return context.measureText(text).width;
  };

  // columnWidth 내에 표시할 태그 계산
  const visibleTags: BuildingFacilityTagModel[] = [];
  let currentWidth = 0;
  const counterSpace = 30; // "+N" 고정 너비
  const containerPaddingX = 20;

  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i];
    const tagWidth = measureTextWidth(tag.name) + fontStyle.padding + fontStyle.gap;

    // 첫 번째 태그는 무조건 표시
    if (i === 0) {
      visibleTags.push(tag);
      currentWidth += tagWidth;
      continue;
    }

    // 두 번째 태그부터는 남은 태그가 있으면 카운터 공간 확보
    const hasMoreTags = i < tags.length - 1;

    if (currentWidth + tagWidth + containerPaddingX + (hasMoreTags ? counterSpace : 0) <= columnWidth) {
      visibleTags.push(tag);
      currentWidth += tagWidth;
    } else {
      break; // 더 이상 공간 없음
    }
  }

  // 남은 태그 개수
  const remainingCount = tags.length - visibleTags.length;
  return (
    <div className="flex-center cursor-pointer">
      <BaseTooltip
        type="tags"
        tooltip={
          // 툴팁 표시
          <div className="flex-center gap5 flex-wrap">
            {tags.map((tag) => (
              <TagChip key={tag.id} tag={tag} />
            ))}
          </div>
        }
      >
        {/* 테이블 > 태그 표시 */}
        <div ref={measureRef} className="flex-center gap5">
          {visibleTags.map((tag, idx) => (
            <div key={tag.id} style={{ maxWidth: idx === visibleTags.length - 1 && remainingCount > 0 ? "190px" : "auto" }}>
              <TagChip tag={tag} className={idx === visibleTags.length - 1 && remainingCount > 0 ? "ellipsis" : "no-wrap"} />
            </div>
          ))}
          {remainingCount > 0 && <span className="no-wrap">+{remainingCount}</span>}
        </div>
      </BaseTooltip>
    </div>
  );
};

export default FacilityTagChips;
